import React, { useCallback, useState } from 'react';
import { Paper, Icon, Button, IconButton, makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { usePropValue, inputStateSetterHelper, useModePanelStyle, useModeFormStyle, FontIcon, BaseInfoCompProps, BaseEntityField,
    BaseInfoCompSaveAction, BaseInfoCompActionsSet, InfoCompPanelHeader, ModeInfoCompTextInputField } from '@moderepo/mode-app-components';



const useStyle = makeStyles((theme: Theme) => {
    return {

        ticketSystemSettings: {
            flexBasis    : 'auto',
            display      : 'flex',
            flexDirection: 'column',
            flex         : 1,
            width        : '100%',
        },

        ticketSystemSettingsNoSettingMessage: {
            fontSize  : 'larger',
            fontWeight: 'bold',
            width     : '100%',
            textAlign : 'center',
            padding   : theme.spacing(2),
        },

        ticketSystemSettingsContainer: {
            border      : `1px solid ${theme.palette.divider}`,
            borderRadius: 4,
            marginBottom: theme.spacing(2),
            position    : 'relative',
            '& .legend' : {
                padding   : theme.spacing(0, 1),
                fontWeight: 'bold',
                fontSize  : 'larger',
            },
            '& .remove-button': {
                color    : theme.palette.secondary.main,
                position : 'absolute',
                top      : -10,
                right    : 0,
                transform: 'translate3d(50%, -50%, 0)',
                zIndex   : 2,
            },
        },

        ticketSystemSettingsFieldMappingTitle: {
            fontWeight: 500,
            fontSize  : 'larger',
            margin    : theme.spacing(2, 0, 1, 0),
        },

        ticketSystemSettingsActionsContainer: {
            textAlign: 'center',
            padding  : theme.spacing(0),
        },

        ticketSystemSettingsAction: {
            margin       : theme.spacing(1),
            textTransform: 'none',
        },
    };
});


/**
 * This is the interface of the set of input fields' values that will be passed to the onClick handler when the 'saveUpdate' button is clicked.
 */
export interface RCTicketSystemSettingsUpdatableInputs {
    readonly salesforce?: {
        readonly clientId: string | undefined;
        readonly clientSecret: string | undefined;
        readonly username: string | undefined;
        readonly password: string | undefined;
        readonly mappingObject: string | undefined;
        readonly mappingDescription: string | undefined;
        readonly mappingSeverity: string | undefined;
        readonly mappingOriginValue: string | undefined;
    } | undefined;
    readonly asana?: {} | undefined;
    readonly jira?: {} | undefined;
}


/**
 * This component has a custom saveUpdate button's onClick input params therefore we need to extends the BaseInfoCompActionsSet and
 * override the 'saveUpdate' action.
 */
export interface RCTicketSystemSettingsActionsSet extends BaseInfoCompActionsSet {
    readonly saveUpdate: BaseInfoCompSaveAction<RCTicketSystemSettingsUpdatableInputs>
}


export interface RCTicketSystemSettingsCompProps extends BaseInfoCompProps {
    readonly fields: {
        readonly supportedTicketSystems: {
            readonly salesforce: {
                readonly clientId: BaseEntityField<string>;
                readonly clientSecret: BaseEntityField<string>;
                readonly username: BaseEntityField<string>;
                readonly password: BaseEntityField<string>;
                readonly mappingObject: BaseEntityField<string>;
                readonly mappingDescription: BaseEntityField<string>;
                readonly mappingSeverity: BaseEntityField<string>;
                readonly mappingOriginValue: BaseEntityField<string>;
                readonly enabled: BaseEntityField<boolean>;
            };
            readonly asana: {
                readonly enabled: BaseEntityField<boolean>;
            };
            readonly jira: {
                readonly enabled: BaseEntityField<boolean>;
            };
        }
    }
    readonly actions?: RCTicketSystemSettingsActionsSet | undefined;
    readonly texts: {
        readonly ticketSystemSettingsNoSystemMessage: String;
        readonly ticketSystemSettingsTitle: string;
        readonly salesforceSettingsTitle: string;
        readonly asanaSettingsTitle: string;
        readonly jiraSettingsTitle: string;
        readonly salesforceSettingsMappingTitle: string;
        readonly setUpSalesforce: string;
        readonly setUpAsana: string;
        readonly setUpJira: string;
    }


    readonly addSalesforceTicketSystem: ()=> void;
    readonly removeSalesforceTicketSystem: ()=> void;
    readonly addAsanaTicketSystem: ()=> void;
    readonly removeAsanaTicketSystem: ()=> void;
    readonly addJiraTicketSystem: ()=> void;
    readonly removeJiraTicketSystem: ()=> void;
}


/**
 * This is a DUMB component used for displaying robot cloud project info. This component does not have any logic. All it does it display
 * data based on the props provided by the container. All action done in this component will be dispatched back to the
 * container's handler.
 */
export const RCTicketSystemSettingsComp: React.FC<RCTicketSystemSettingsCompProps> = (props: RCTicketSystemSettingsCompProps) => {
    // Flags to enable/disable ticket systems
    const [supportSalesforce, setSupportSalesforce] = useState<boolean>(props.fields.supportedTicketSystems.salesforce.enabled.value || false);
    const [supportAsana, setSupportAsana] = useState<boolean>(props.fields.supportedTicketSystems.asana.enabled.value || false);
    const [supportJira, setSupportJira] = useState<boolean>(props.fields.supportedTicketSystems.jira.enabled.value || false);

    // Input states for Salesforce settings
    const [sfClientIdInput, setSFClientIdInput] = usePropValue(props.fields.supportedTicketSystems.salesforce.clientId.value);
    const [sfClientSecretInput, setSFClientSecretInput] = usePropValue(props.fields.supportedTicketSystems.salesforce.clientSecret.value);
    const [sfUsernameInput, setSFUsernameInput] = usePropValue(props.fields.supportedTicketSystems.salesforce.username.value);
    const [sfPasswordInput, setSFPasswordInput] = usePropValue(props.fields.supportedTicketSystems.salesforce.password.value);
    const [sfMappingObjectInput, setSFMappingObjectInput] = usePropValue(
        props.fields.supportedTicketSystems.salesforce.mappingObject.value,
    );
    const [sfMappingDescriptionInput, setSFMappingDescriptionInput] = usePropValue(
        props.fields.supportedTicketSystems.salesforce.mappingDescription.value,
    );
    const [sfMappingSeverityInput, setSFMappingSeverityInput] = usePropValue(
        props.fields.supportedTicketSystems.salesforce.mappingSeverity.value,
    );
    const [sfMappingOriginValueInput, setSFMappingOriginValueInput] = usePropValue(
        props.fields.supportedTicketSystems.salesforce.mappingOriginValue.value,
    );


    const classes = useStyle();


    /**
     * On input change handler for all the inputs. When an input change, call the onInputChangeHelper and pass it the setter functions
     * for all inputs. The helper function will call the setter function accordingly based on the input name.
     */
    const onInputChange = useCallback((inputName: string, value: any) => {
        inputStateSetterHelper(inputName, value, {
            'supportedTicketSystems.salesforce.clientId'    : setSFClientIdInput,
            'supportedTicketSystems.salesforce.clientSecret': setSFClientSecretInput,
            'supportedTicketSystems.salesforce.username'    : setSFUsernameInput,
            'supportedTicketSystems.salesforce.password'    : setSFPasswordInput,

            'supportedTicketSystems.salesforce.mappingObject'     : setSFMappingObjectInput,
            'supportedTicketSystems.salesforce.mappingDescription': setSFMappingDescriptionInput,
            'supportedTicketSystems.salesforce.mappingSeverity'   : setSFMappingSeverityInput,
            'supportedTicketSystems.salesforce.mappingOriginValue': setSFMappingOriginValueInput,
        });
    }, [setSFClientIdInput, setSFClientSecretInput, setSFUsernameInput, setSFPasswordInput, setSFMappingObjectInput, setSFMappingDescriptionInput,
        setSFMappingSeverityInput, setSFMappingOriginValueInput]);



    const panelClasses = useModePanelStyle();
    const formClasses = useModeFormStyle();


    /**
     * On cancel edit, we need to reverse all the input back to the original values
     */
    const reverseFormInput = useCallback(() => {
        // Reset Ticket System settings
        setSupportSalesforce(props.fields.supportedTicketSystems.salesforce.enabled.value || false);
        setSFClientIdInput(props.fields.supportedTicketSystems.salesforce.clientId.value);
        setSFClientSecretInput(props.fields.supportedTicketSystems.salesforce.clientId.value);
        setSFUsernameInput(props.fields.supportedTicketSystems.salesforce.username.value);
        setSFPasswordInput(props.fields.supportedTicketSystems.salesforce.password.value);
        setSFMappingObjectInput(props.fields.supportedTicketSystems.salesforce.mappingObject.value);
        setSFMappingDescriptionInput(props.fields.supportedTicketSystems.salesforce.mappingDescription.value);
        setSFMappingSeverityInput(props.fields.supportedTicketSystems.salesforce.mappingSeverity.value);
        setSFMappingOriginValueInput(props.fields.supportedTicketSystems.salesforce.mappingOriginValue.value);

        setSupportAsana(props.fields.supportedTicketSystems.asana.enabled.value || false);

        setSupportJira(props.fields.supportedTicketSystems.jira.enabled.value || false);

    }, [props, setSupportSalesforce, setSFClientIdInput, setSFClientSecretInput, setSFUsernameInput, setSFPasswordInput, setSFMappingObjectInput,
        setSFMappingDescriptionInput, setSFMappingSeverityInput, setSFMappingOriginValueInput, setSupportAsana, setSupportJira]);



    /**
     * On form submit. Collect the values for all input and pass it back to the container
     */
    const onFormSubmit = useCallback(async (event: React.FormEvent) => {
        event.stopPropagation();
        event.preventDefault();

        if (props.actions?.saveUpdate) {
            props.actions.saveUpdate.onClick({
                salesforce: supportSalesforce ? {
                    clientId          : sfClientIdInput?.trim(),
                    clientSecret      : sfClientSecretInput?.trim(),
                    username          : sfUsernameInput?.trim(),
                    password          : sfPasswordInput?.trim(),
                    mappingObject     : sfMappingObjectInput?.trim(),
                    mappingDescription: sfMappingDescriptionInput?.trim(),
                    mappingSeverity   : sfMappingSeverityInput?.trim(),
                    mappingOriginValue: sfMappingOriginValueInput?.trim(),
                } : undefined,
                asana: supportAsana ? {} : undefined,
                jira : supportJira ? {} : undefined,
            });
        }


        return false;
    }, [props, sfClientIdInput, sfClientSecretInput, sfUsernameInput, sfPasswordInput, supportSalesforce, sfMappingObjectInput,
        sfMappingDescriptionInput, sfMappingSeverityInput, sfMappingOriginValueInput, supportAsana, supportJira]);


    const addSalesforceTicketSystem = useCallback(() => {
        setSupportSalesforce(true);
        props.addSalesforceTicketSystem();
    }, [setSupportSalesforce, props]);

    const removeSalesforceTicketSystem = useCallback(() => {
        setSupportSalesforce(false);
        props.removeSalesforceTicketSystem();
    }, [setSupportSalesforce, props]);

    const addAsanaTicketSystem = useCallback(() => {
        setSupportAsana(true);
        props.addAsanaTicketSystem();
    }, [setSupportAsana, props]);

    const removeAsanaTicketSystem = useCallback(() => {
        setSupportAsana(false);
        props.removeAsanaTicketSystem();
    }, [setSupportAsana, props]);

    const addJiraTicketSystem = useCallback(() => {
        setSupportJira(true);
        props.addJiraTicketSystem();
    }, [setSupportJira, props]);

    const removeJiraTicketSystem = useCallback(() => {
        setSupportJira(false);
        props.removeJiraTicketSystem();
    }, [setSupportJira, props]);



    return (
        <Paper
            elevation={2}
            className={clsx(panelClasses.root, props.className)}
        >
            <InfoCompPanelHeader {...props} />
            <div className={clsx(panelClasses.panelContent)}>
                <form
                    className={clsx(formClasses.root, props.formProps?.className)}
                    noValidate
                    autoComplete="on"
                    method="POST"
                    onSubmit={onFormSubmit}
                >
                    <div className={classes.ticketSystemSettings}>
                        {!supportSalesforce && !supportAsana && !supportJira && (
                            <div className={classes.ticketSystemSettingsNoSettingMessage}>{props.texts.ticketSystemSettingsNoSystemMessage}</div>
                        )}
                        {(supportSalesforce || supportAsana || supportJira) && (
                            <div className={classes.ticketSystemSettingsNoSettingMessage}>{props.texts.ticketSystemSettingsTitle}</div>
                        )}

                        {supportSalesforce && (
                            <fieldset className={classes.ticketSystemSettingsContainer}>
                                <legend className="legend">{props.texts.salesforceSettingsTitle}</legend>
                                <IconButton className="remove-button" onClick={removeSalesforceTicketSystem}><Icon>cancel</Icon></IconButton>
                                <div className={clsx(formClasses.formInputGroup)}>
                                    <ModeInfoCompTextInputField
                                        className={formClasses.formInputField}
                                        inputType="text"
                                        multiline
                                        compProps={props}
                                        inputProps={props.fields.supportedTicketSystems.salesforce.clientId}
                                        fieldName="supportedTicketSystems.salesforce.clientId"
                                        inputValue={sfClientIdInput}
                                        onInputChange={onInputChange}
                                    />
                                    <ModeInfoCompTextInputField
                                        className={formClasses.formInputField}
                                        inputType="text"
                                        multiline
                                        compProps={props}
                                        inputProps={props.fields.supportedTicketSystems.salesforce.clientSecret}
                                        fieldName="supportedTicketSystems.salesforce.clientSecret"
                                        inputValue={sfClientSecretInput}
                                        onInputChange={onInputChange}
                                    />
                                </div>
                                <div className={clsx(formClasses.formInputGroup)}>
                                    <ModeInfoCompTextInputField
                                        className={formClasses.formInputField}
                                        inputType="text"
                                        compProps={props}
                                        inputProps={props.fields.supportedTicketSystems.salesforce.username}
                                        fieldName="supportedTicketSystems.salesforce.username"
                                        inputValue={sfUsernameInput}
                                        onInputChange={onInputChange}
                                    />
                                    <ModeInfoCompTextInputField
                                        className={formClasses.formInputField}
                                        inputType="text"
                                        compProps={props}
                                        inputProps={props.fields.supportedTicketSystems.salesforce.password}
                                        fieldName="supportedTicketSystems.salesforce.password"
                                        inputValue={sfPasswordInput}
                                        onInputChange={onInputChange}
                                    />
                                </div>
                                <div className={classes.ticketSystemSettingsFieldMappingTitle}>
                                    {props.texts.salesforceSettingsMappingTitle}
                                </div>
                                <div className={clsx(formClasses.formInputGroup, 'small')}>
                                    <ModeInfoCompTextInputField
                                        className={formClasses.formInputField}
                                        inputType="text"
                                        compProps={props}
                                        inputProps={props.fields.supportedTicketSystems.salesforce.mappingObject}
                                        fieldName="supportedTicketSystems.salesforce.mappingObject"
                                        inputValue={sfMappingObjectInput}
                                        onInputChange={onInputChange}
                                    />
                                    <ModeInfoCompTextInputField
                                        className={formClasses.formInputField}
                                        inputType="text"
                                        compProps={props}
                                        inputProps={props.fields.supportedTicketSystems.salesforce.mappingDescription}
                                        fieldName="supportedTicketSystems.salesforce.mappingDescription"
                                        inputValue={sfMappingDescriptionInput}
                                        onInputChange={onInputChange}
                                    />
                                    <ModeInfoCompTextInputField
                                        className={formClasses.formInputField}
                                        inputType="text"
                                        compProps={props}
                                        inputProps={props.fields.supportedTicketSystems.salesforce.mappingSeverity}
                                        fieldName="supportedTicketSystems.salesforce.mappingSeverity"
                                        inputValue={sfMappingSeverityInput}
                                        onInputChange={onInputChange}
                                    />
                                    <ModeInfoCompTextInputField
                                        className={formClasses.formInputField}
                                        inputType="text"
                                        compProps={props}
                                        inputProps={props.fields.supportedTicketSystems.salesforce.mappingOriginValue}
                                        fieldName="supportedTicketSystems.salesforce.mappingOriginValue"
                                        inputValue={sfMappingOriginValueInput}
                                        onInputChange={onInputChange}
                                    />
                                </div>
                            </fieldset>
                        )}
                        {supportAsana && (
                            <fieldset className={classes.ticketSystemSettingsContainer}>
                                <legend className="legend">{props.texts.asanaSettingsTitle}</legend>
                                <IconButton className="remove-button" onClick={removeAsanaTicketSystem}><Icon>cancel</Icon></IconButton>
                                <div className={clsx(formClasses.formInputGroup)}>
                                    <div>Not Yet Supported</div>
                                </div>
                            </fieldset>
                        )}
                        {supportJira && (
                            <fieldset className={classes.ticketSystemSettingsContainer}>
                                <legend className="legend">{props.texts.jiraSettingsTitle}</legend>
                                <IconButton className="remove-button" onClick={removeJiraTicketSystem}><Icon>cancel</Icon></IconButton>
                                <div className={clsx(formClasses.formInputGroup)}>
                                    <div>Not Yet Supported</div>
                                </div>
                            </fieldset>
                        )}

                        <div className={classes.ticketSystemSettingsActionsContainer}>
                            {!supportSalesforce && (
                                <Button
                                    className={classes.ticketSystemSettingsAction}
                                    color="primary"
                                    variant="contained"
                                    onClick={addSalesforceTicketSystem}
                                >
                                    {props.texts.setUpSalesforce}
                                </Button>
                            )}
                            {!supportAsana && (
                                <Button
                                    className={classes.ticketSystemSettingsAction}
                                    color="primary"
                                    variant="contained"
                                    disabled
                                    onClick={addAsanaTicketSystem}
                                >
                                    {props.texts.setUpAsana}
                                </Button>
                            )}
                            {!supportJira && (
                                <Button
                                    className={classes.ticketSystemSettingsAction}
                                    color="primary"
                                    variant="contained"
                                    disabled
                                    onClick={addJiraTicketSystem}
                                >
                                    {props.texts.setUpJira}
                                </Button>
                            )}
                        </div>
                    </div>

                    {props.isEditing && props.actions && (
                        <div className={clsx(formClasses.formActionContainer)}>
                            <div />
                            <div>
                                {props.actions?.cancelEdit && (
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        size="small"
                                        startIcon={props.actions.cancelEdit.icon
                                            ? <FontIcon iconName={props.actions.cancelEdit.icon} /> : undefined}
                                        onClick={() => {
                                            reverseFormInput();
                                            if (props.actions?.cancelEdit) {
                                                props.actions.cancelEdit.onClick();
                                            }
                                        }}
                                    >
                                        {props.actions.cancelEdit.label}
                                    </Button>
                                )}
                                {props.actions?.saveUpdate && (
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        size="small"
                                        startIcon={props.actions.saveUpdate.icon
                                            ? <FontIcon iconName={props.actions.saveUpdate.icon} /> : undefined}
                                    >
                                        {props.actions.saveUpdate.label}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </form>
            </div>

        </Paper>
    );

};
